import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useT } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { ArrowRight, Briefcase, CreditCard, PaperPlaneTilt, Scan, Wallet, DeviceMobile, Heart, ShieldCheck } from "@phosphor-icons/react";
import SocialProofCarousel from "../components/SocialProofCarousel";
import CardsInFlight from "../components/CardsInFlight";
import TrustStrip from "../components/TrustStrip";
import NoteWordCloud from "../components/NoteWordCloud";
import DownloadAppButton from "../components/DownloadAppButton";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { toast } from "sonner";
import { AsYouType, parsePhoneNumberFromString, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

const HERO = "https://images.pexels.com/photos/33101042/pexels-photo-33101042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const fmtPct = (n) => `${Number(n).toFixed(Number.isInteger(Number(n)) ? 0 : 1)}%`;

export default function Landing() {
  useSeo({
    title: "Fund a virtual card. Ship it anywhere.",
    description: "Issue a tap-and-pay virtual debit card with a scannable QR. Stripe-powered checkout, USD-denominated balance, transparent fees — delivered by email or download.",
    path: "/",
  });
  const t = useT();
  const [rates, setRates] = useState(null);
  useEffect(() => {
    api.get("/public/rates").then((r) => setRates(r.data)).catch(() => {});
    // If the visitor arrived with ?ref=<code>, store it for the eventual signup.
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) sessionStorage.setItem("ref_code", ref.toLowerCase());
    } catch { /* ignore */ }
  }, []);
  const svc = rates ? fmtPct(rates.service_fee_percent) : "3.5%";
  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-20 md:pt-20 md:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-6">{t("landing.hero.eyebrow")}</p>
            <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#112318]">
              {t("landing.hero.title_a")}<br />
              <span className="italic text-[#184A30]">{t("landing.hero.title_b_ship")}</span> {t("landing.hero.title_b_rest")}<br />
              {t("landing.hero.title_c")} <span className="text-[#D96C4A]">USD</span>.
            </h1>
            <p className="mt-8 max-w-xl text-[#4A5C50] text-lg leading-relaxed">
              {t("landing.hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/register">
                <Button size="lg" className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white px-8 h-12" data-testid="hero-cta-start">
                  {t("landing.hero.cta_start")} <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <RequestCashButton />
              <DownloadAppButton />
              <Link to="/rates">
                <Button size="lg" variant="outline" className="rounded-xl border-[#184A30] text-[#184A30] h-12" data-testid="hero-cta-rates">
                  {t("landing.hero.cta_rates")}
                </Button>
              </Link>
            </div>
            <FindMyCardButton />

            <div className="mt-6">
              <Link
                to="/agent/apply"
                className="inline-flex items-center gap-2 text-sm text-[#184A30] hover:text-[#0F3320] underline underline-offset-4 decoration-dotted"
                data-testid="hero-cta-become-agent"
              >
                <Briefcase size={14} weight="duotone" />
                Earn 0.5% commission · Become a Zcardon agent →
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-8 max-w-lg">
              <Stat label={t("landing.hero.stat_currency")} value="USD" />
              <Stat label={t("landing.hero.stat_settle")} value={t("landing.hero.stat_settle_value")} />
              <Stat label={t("landing.hero.stat_service_fee")} value={svc} testId="stat-service-fee" />
            </div>
          </div>

          <div className="lg:col-span-5 fade-up-d2 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={HERO} alt="City skyline" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F16]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-[#FBF9F6]">
                <p className="text-[10px] tracking-[0.3em] opacity-80">{t("landing.hero.image_eyebrow")}</p>
                <p className="serif text-2xl mt-1">{t("landing.hero.image_title")}</p>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#FBF9F6] border border-[#E5E0D8] rounded-2xl p-4 shadow-xl card-lift">
              <p className="text-xs tracking-[0.2em] text-[#4A5C50]">{t("landing.hero.live_pill")}</p>
              <p className="mono text-lg">{t("landing.hero.live_pill_text")}</p>
            </div>
          </div>
        </div>

        {/* 20-sec demo */}
        <DemoPreview />

        {/* Unified trust strip — fuses TrustBadge + carousel + flight widget
            with a shared dark-green theme and one "how we collect this" link. */}
        <TrustStrip />

        {/* What people use it for — anonymized note word cloud */}
        <NoteWordCloud />

        {/* Marquee */}
        <div className="border-y border-[#E5E0D8] bg-[#FBF9F6] py-6">
          <div className="marquee">
            <div className="marquee-track serif text-3xl text-[#184A30]">
              {Array.from({ length: 10 }).map((_, i) => (
                <span key={i} className="flex items-center gap-3">
                  {t("landing.hero.marquee")}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Honest "today vs roadmap" panel — sets accurate expectations for
          new customers before they discover the closed-loop QR vs real
          Visa distinction at checkout, and turns the current limitation
          into a public roadmap story showing momentum. */}
      <section
        className="border-t border-[#E5E0D8] bg-[#FBF9F6] py-16"
        data-testid="payment-evolution-strip"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">How Zcardon works</p>
          <h2 className="serif text-3xl sm:text-4xl text-[#112318] mt-2 max-w-2xl">
            Built for sending money to family — today, and at every Visa terminal soon.
          </h2>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {/* Today */}
            <div className="rounded-2xl border border-[#184A30]/20 bg-white p-6" data-testid="evolution-today">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#184A30]">Today · live</p>
              <h3 className="serif text-2xl mt-1 text-[#112318]">Closed-loop QR card</h3>
              <ul className="mt-4 space-y-2 text-sm text-[#112318]">
                <li className="flex gap-2"><span className="text-[#184A30]">✓</span> Send a virtual card to anyone, anywhere — by email, SMS, or link</li>
                <li className="flex gap-2"><span className="text-[#184A30]">✓</span> Receiver views the card image with number, expiry, CVV, balance</li>
                <li className="flex gap-2"><span className="text-[#184A30]">✓</span> Tap the QR with any phone to charge — peer-to-peer settlement</li>
                <li className="flex gap-2"><span className="text-[#184A30]">✓</span> Real-time balance, charges, and receiver notifications</li>
              </ul>
              <p className="text-xs text-[#4A5C50] mt-4 leading-relaxed">
                Perfect for handing money to family abroad and for Zcardon-aware merchants. Doesn't work at chain retailers (Starbucks, Target, gas pumps) — that needs the real card network.
              </p>
            </div>

            {/* Coming */}
            <div className="rounded-2xl border border-[#D96C4A]/30 bg-gradient-to-br from-[#FFF1E8] to-white p-6" data-testid="evolution-coming">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D96C4A]">Q3 2026 · in approval</p>
              <h3 className="serif text-2xl mt-1 text-[#112318]">Real Visa virtual debit</h3>
              <ul className="mt-4 space-y-2 text-sm text-[#112318]">
                <li className="flex gap-2"><span className="text-[#D96C4A]">→</span> Tap-to-pay at any Visa terminal worldwide (Starbucks, Uber, ATMs)</li>
                <li className="flex gap-2"><span className="text-[#D96C4A]">→</span> Use online — Amazon, food delivery, subscriptions</li>
                <li className="flex gap-2"><span className="text-[#D96C4A]">→</span> Real card on Stripe's network · PCI-compliant PAN reveal</li>
                <li className="flex gap-2"><span className="text-[#D96C4A]">→</span> Add to Apple Wallet / Google Pay (native app, separate launch)</li>
              </ul>
              <p className="text-xs text-[#4A5C50] mt-4 leading-relaxed">
                We're going through Stripe Issuing's gated approval right now. Every closed-loop card you mint today auto-upgrades to a real Visa card when we ship — no re-issuance, same receiver, same number you remember.
              </p>

              {/* Waitlist capture — drops email into tap_to_pay_waitlist
                  collection. Shows the running size as a credibility
                  signal once we have at least 5 signups. */}
              <TapToPayWaitlistForm />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-10 text-center text-sm text-[#4A5C50]">
        <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-3 mb-3">
          <Link to="/privacy" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/terms" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-terms">Terms of Service</Link>
          <Link to="/sms-policy" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-sms-policy">SMS Policy</Link>
        </div>
        <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2 mb-3">
          <Link to="/rates" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-rates">{t("landing.footer.rates")}</Link>
          <Link to="/support" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-support">{t("landing.footer.support")}</Link>
          <Link to="/login?role=agent" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-agent-portal">Agent portal</Link>
          <Link to="/login?role=admin" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-admin-portal">{t("landing.footer.admin_portal")}</Link>
        </div>
        <div className="text-xs text-[#4A5C50]" data-testid="footer-legal-entity">
          Zcardon is a product of <span className="font-semibold text-[#184A30]">Zeborg Water Solution Corp.</span>
        </div>
        <div className="mt-1">{t("landing.footer.copy")}</div>
      </footer>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="serif text-4xl text-[#184A30]">{value}</p>
    <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-1">{label}</p>
  </div>
);

function DemoPreview() {
  const t = useT();
  const [step, setStep] = useState(0);
  const steps = [
    { Icon: CreditCard, label: t("landing.demo.step_issue.label"), detail: t("landing.demo.step_issue.detail") },
    { Icon: PaperPlaneTilt, label: t("landing.demo.step_share.label"), detail: t("landing.demo.step_share.detail") },
    { Icon: Scan, label: t("landing.demo.step_scan.label"), detail: t("landing.demo.step_scan.detail") },
    { Icon: Wallet, label: t("landing.demo.step_spend.label"), detail: t("landing.demo.step_spend.detail") },
  ];
  React.useEffect(() => {
    const tm = setInterval(() => setStep((s) => (s + 1) % steps.length), 2400);
    return () => clearInterval(tm);
  }, [steps.length]);

  return (
    <section id="demo" className="border-t border-[#E5E0D8] bg-[#0A1F16] text-[#FBF9F6]" data-testid="demo-preview">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 fade-up">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#8BA888]">{t("landing.demo.eyebrow")}</p>
          <h2 className="serif text-4xl md:text-5xl leading-tight mt-4">
            {t("landing.demo.title_a")} <span className="italic text-[#8BA888]">{t("landing.demo.title_b_spend")}</span>{t("landing.demo.title_c")}
          </h2>
          <div className="mt-8 space-y-3">
            {steps.map((s, i) => {
              const active = i === step;
              return (
                <div
                  key={s.label}
                  className={`flex items-center gap-4 rounded-xl px-4 py-3 border transition-all duration-300 ${
                    active
                      ? "border-[#8BA888] bg-[#8BA888]/10"
                      : "border-white/10 opacity-60"
                  }`}
                  data-testid={`demo-step-${i}`}
                >
                  <span
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                      active ? "bg-[#8BA888] text-[#0A1F16]" : "bg-white/10 text-white/60"
                    }`}
                  >
                    <s.Icon size={18} weight="duotone" />
                  </span>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${active ? "text-[#FBF9F6]" : "text-white/70"}`}>{s.label}</p>
                    <p className={`text-xs mt-0.5 ${active ? "text-white/80" : "text-white/40"}`}>{s.detail}</p>
                  </div>
                  <span className={`mono text-xs ${active ? "text-[#8BA888]" : "text-white/30"}`}>0{i + 1}</span>
                </div>
              );
            })}
          </div>
          <Link to="/register" className="inline-flex items-center gap-2 mt-8 text-[#8BA888] text-sm tracking-widest uppercase border-b border-[#8BA888]/40 hover:border-[#8BA888] pb-1" data-testid="demo-cta">
            {t("landing.demo.cta_try")} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="lg:col-span-7 fade-up-d2">
          <div className="relative rounded-2xl overflow-hidden aspect-video bg-gradient-to-br from-[#112318] via-[#0F3320] to-[#184A30] border border-white/10">
            <DemoScene activeStep={step} />
            <div className="absolute top-4 left-4 flex items-center gap-2 z-20">
              <span className="w-2 h-2 rounded-full bg-[#D96C4A] animate-pulse" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/70">{t("landing.demo.live_pill")}</span>
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex gap-2 z-20">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-0.5 rounded-full transition-colors ${
                    i === step ? "bg-[#8BA888]" : "bg-white/15"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoScene({ activeStep }) {
  const t = useT();
  return (
    <>
      <div className="absolute inset-0" style={{
        backgroundImage: "radial-gradient(circle at 30% 40%, rgba(139,168,136,0.15), transparent 60%), radial-gradient(circle at 70% 70%, rgba(217,108,74,0.12), transparent 55%)"
      }} />

      <Scene active={activeStep === 0}>
        <div className="demo-card-animate relative w-[68%] aspect-[1.6/1]">
          <MiniCard />
        </div>
        <div className="absolute bottom-12 left-0 right-0 text-center text-[#8BA888] mono text-xs tracking-widest uppercase fade-text">{t("landing.demo.scene1_caption")}</div>
      </Scene>

      <Scene active={activeStep === 1}>
        <div className="relative w-[55%] aspect-[1.6/1] translate-y-2">
          <MiniCard />
        </div>
        <span className="demo-plane absolute text-[#8BA888]" style={{ fontSize: 28 }}>✈</span>
        <div className="absolute bottom-12 left-0 right-0 text-center text-[#8BA888] mono text-xs tracking-widest uppercase fade-text">{t("landing.demo.scene2_caption")}</div>
      </Scene>

      <Scene active={activeStep === 2}>
        <div className="relative flex items-center justify-center gap-8">
          <div className="relative w-[140px] h-[210px] rounded-[20px] border-2 border-white/30 bg-[#0A1F16] flex items-center justify-center shadow-2xl">
            <div className="w-[80px] h-[80px] bg-white rounded-md relative overflow-hidden">
              <QrPattern />
              <div className="scan-line absolute left-0 right-0 h-[2px] bg-[#D96C4A] shadow-[0_0_8px_#D96C4A]" />
            </div>
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-1 rounded bg-white/40" />
          </div>
        </div>
        <div className="absolute bottom-12 left-0 right-0 text-center text-[#8BA888] mono text-xs tracking-widest uppercase fade-text">{t("landing.demo.scene3_caption")}</div>
      </Scene>

      <Scene active={activeStep === 3}>
        <div className="text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/60">{t("landing.demo.scene4_eyebrow")}</p>
          <p className="serif text-6xl text-[#8BA888] mt-3 count-up">$175.00</p>
          <p className="text-xs text-white/70 mt-3 mono">{t("landing.demo.scene4_status")}</p>
        </div>
      </Scene>

      <style>{`
        @keyframes demo-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes demo-card-spin { 0% { transform: rotateY(-60deg) scale(0.8); opacity: 0; } 50% { opacity: 1; } 100% { transform: rotateY(0) scale(1); opacity: 1; } }
        @keyframes demo-plane { 0% { left: 5%; top: 45%; opacity: 0; } 20% { opacity: 1; } 100% { left: 100%; top: 15%; opacity: 0; } }
        @keyframes demo-scan { 0% { top: 0; } 50% { top: 100%; } 100% { top: 0; } }
        @keyframes demo-count { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .demo-scene { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity 0.5s; }
        .demo-scene.active { opacity: 1; animation: demo-fade-in 0.5s ease-out; }
        .demo-card-animate { animation: demo-card-spin 1.2s ease-out; }
        .demo-plane { animation: demo-plane 2.3s ease-in-out infinite; }
        .scan-line { animation: demo-scan 1.6s ease-in-out infinite; }
        .count-up { animation: demo-count 0.8s ease-out; }
        .fade-text { animation: demo-fade-in 0.8s ease-out 0.4s backwards; }
      `}</style>
    </>
  );
}

function Scene({ active, children }) {
  return <div className={`demo-scene ${active ? "active" : ""}`}>{children}</div>;
}

function MiniCard() {
  return (
    <div className="w-full h-full rounded-xl p-4 shadow-2xl relative overflow-hidden"
         style={{ background: "linear-gradient(135deg, #184A30 0%, #0A1F16 100%)" }}>
      <div className="absolute top-3 right-3 text-[#8BA888] text-[9px] tracking-widest">CARDREMIT</div>
      <div className="absolute top-3 left-3 w-8 h-6 rounded-sm bg-gradient-to-br from-[#8BA888]/80 to-[#8BA888]/30" />
      <div className="absolute bottom-8 left-4 right-4 font-mono text-white text-sm tracking-widest">4989 • 0168 • 2598 • 3196</div>
      <div className="absolute bottom-3 left-4 text-[9px] text-white/70 tracking-widest">JANE DOE · 04/29</div>
      <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-[#D96C4A]/80 flex items-center justify-center text-white text-[9px]">USD</div>
    </div>
  );
}

function QrPattern() {
  return (
    <div className="absolute inset-0 grid grid-cols-7 grid-rows-7 gap-[2px] p-1">
      {Array.from({ length: 49 }).map((_, i) => {
        const on = [0,1,2,5,6,8,13,14,16,17,19,21,23,24,26,28,30,32,34,36,38,41,42,43,46,47,48].includes(i);
        return <div key={i} className={on ? "bg-[#0A1F16]" : ""} />;
      })}
    </div>
  );
}


function RequestCashButton() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(() => detectCountry());
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(50);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  // Three-stage flow:
  //   stage 'form'   — collect phone/amount, send OTP
  //   stage 'otp'    — collect 6-digit code
  //   stage 'paid'   — show Stripe checkout button + receipt
  const [stage, setStage] = useState("form");
  const [otpRequestId, setOtpRequestId] = useState(null);
  const [otpToPhone, setOtpToPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [result, setResult] = useState(null); // { card_id, checkout_url, to_phone, amount, total_charged }

  // Auto-open + prefill from URL params, used by the static landing demo at
  // zcardon.com which redirects to `/?prefill_phone=+1XXX…&prefill_amount=NN`.
  // We parse the phone via libphonenumber so the country flag + local digits
  // match the user's typed input exactly. UTM params get cached for the
  // eventual conversion event.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const phoneRaw = params.get("prefill_phone");
      const amountRaw = params.get("prefill_amount");
      const utmSource = params.get("utm_source");
      const utmMedium = params.get("utm_medium");
      if (!phoneRaw && !amountRaw) return;
      if (phoneRaw) {
        const parsed = parsePhoneNumberFromString(phoneRaw);
        if (parsed?.country) setCountry(parsed.country);
        if (parsed?.nationalNumber) {
          setPhone(String(parsed.nationalNumber));
        } else {
          // Best-effort fallback if libphonenumber can't fully parse
          setPhone(phoneRaw.replace(/^\+\d{1,3}/, "").replace(/\D/g, ""));
        }
      }
      if (amountRaw) {
        const n = Number(amountRaw);
        if (Number.isFinite(n) && n >= 1 && n <= 2000) setAmount(n);
      }
      if (utmSource || utmMedium) {
        sessionStorage.setItem(
          "cash_request_utm",
          JSON.stringify({ source: utmSource || "", medium: utmMedium || "", ts: Date.now() })
        );
      }
      setOpen(true);
      // Strip prefill params from the URL so a refresh doesn't loop the dialog
      // open again and the user's address bar stays clean.
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    } catch { /* ignore — non-fatal */ }
  }, []);

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!phone.trim()) { toast.error("Enter your phone number"); return; }
    if (!(Number(amount) > 0)) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      let composed = "";
      try {
        const parsed = parsePhoneNumberFromString(phone, country);
        if (parsed?.isValid()) composed = parsed.number;
      } catch { /* ignore */ }
      if (!composed) {
        const dial = COUNTRIES.find((c) => c.code === country)?.dial || "+1";
        composed = `${dial}${phone.trim().replace(/^[+0]+/, "")}`;
      }
      const r = await api.post("/cash-request/start", {
        phone: composed,
        amount: Number(amount),
        receiver_name: name.trim() || null,
      });
      setOtpRequestId(r.data.request_id);
      setOtpToPhone(r.data.to_phone);
      setOtpResendCooldown(30);
      setStage("otp");
      toast.success("Verification code sent — check your texts");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not text the code");
    } finally {
      setBusy(false);
    }
  };

  // Cooldown ticker for the "Resend code" button. Counts down once a second
  // while > 0; the button only becomes clickable when it hits 0.
  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const t = setTimeout(() => setOtpResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendCooldown]);

  // WebOTP API — Android Chrome/Edge auto-detect the SMS code and fill the
  // input the moment the message arrives, no user interaction needed. The
  // SMS body must end with `@<origin> #<code>` (we set that on the backend).
  // Silently no-op on iOS / desktop / unsupported browsers.
  useEffect(() => {
    if (stage !== "otp") return;
    if (typeof window === "undefined") return;
    if (!("OTPCredential" in window)) return;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((cred) => {
        const code = cred?.code;
        if (code && /^\d{6}$/.test(code)) {
          setOtpCode(code);
          // Auto-submit so the user doesn't even need to tap. Use a microtask
          // so React's controlled input commits the new value first.
          queueMicrotask(() => {
            document
              .querySelector('[data-testid="cash-request-otp-submit"]')
              ?.click();
          });
        }
      })
      .catch(() => {
        // User dismissed or permission denied — fall through to manual entry.
      });
    return () => ac.abort();
  }, [stage]);

  const resendOtp = async () => {
    if (otpResendCooldown > 0 || busy) return;
    await sendOtp(null);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otpCode.trim())) {
      toast.error("Enter the 6-digit code we texted");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/cash-request/verify", {
        request_id: otpRequestId,
        code: otpCode.trim(),
      });
      setResult(r.data);
      setStage("paid");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null); setPhone(""); setAmount(50); setName("");
    setOtpRequestId(null); setOtpCode(""); setOtpToPhone(""); setStage("form");
    setOtpResendCooldown(0);
  };
  const dialCode = COUNTRIES.find((c) => c.code === country)?.dial || "+1";

  // Live "as you type" formatter using libphonenumber-js. Re-runs on every
  // keystroke so the user sees Wise/Revolut-style spacing while typing.
  const formattedPhone = React.useMemo(() => {
    if (!phone) return "";
    try {
      return new AsYouType(country).input(phone);
    } catch {
      return phone;
    }
  }, [phone, country]);

  // Country-specific example for the placeholder (e.g. UK → "07400 123456",
  // US → "(201) 555-0123") sourced from the libphonenumber-js mobile dataset.
  const placeholder = React.useMemo(() => {
    try {
      const ex = getExampleNumber(country, examples);
      return ex ? ex.formatNational() : "555 123 4567";
    } catch {
      return "555 123 4567";
    }
  }, [country]);

  // Validity badge — turns green once libphonenumber says it's a valid number.
  const isValid = React.useMemo(() => {
    if (!phone) return null;
    try {
      const parsed = parsePhoneNumberFromString(phone, country);
      return parsed?.isValid() ? true : false;
    } catch {
      return false;
    }
  }, [phone, country]);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          variant="outline"
          className="rounded-xl border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white h-12"
          data-testid="hero-cta-request-cash"
        >
          <DeviceMobile size={18} weight="duotone" className="mr-2" /> {t("landing.hero.cta_request_cash")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-[#FBF9F6] border-[#E5E0D8]" data-testid="cash-request-dialog">
        {stage === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="serif text-3xl">{t("landing.cash.dialog_title")}</DialogTitle>
              <DialogDescription className="text-[#4A5C50]">
                {t("landing.cash.dialog_desc")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={sendOtp} className="space-y-4 py-2">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Your phone number</Label>
                <div className="flex gap-2 mt-1">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="rounded-lg border border-[#E5E0D8] bg-white px-2 py-2 text-sm mono w-28"
                    data-testid="cash-request-country"
                    aria-label="Country code"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.dial}
                      </option>
                    ))}
                  </select>
                  <div className="flex-1 relative">
                    <input
                      type="tel"
                      className="underline-input mono w-full pr-7"
                      placeholder={placeholder}
                      value={formattedPhone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="cash-request-phone"
                      required
                    />
                    {isValid !== null && (
                      <span
                        className={`absolute right-1 top-1/2 -translate-y-1/2 text-xs ${
                          isValid ? "text-[#184A30]" : "text-[#B04E2F]"
                        }`}
                        data-testid="cash-request-phone-validity"
                        aria-label={isValid ? "Valid number" : "Invalid number"}
                      >
                        {isValid ? "✓" : "·"}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-[#4A5C50] mt-1 mono">
                  {phone
                    ? <>Will text {dialCode} {formattedPhone}</>
                    : <>e.g. {placeholder}</>}
                </p>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Amount (USD)</Label>
                <input
                  type="number" min="1" max="2000" step="1"
                  className="underline-input mono text-2xl"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="cash-request-amount"
                  required
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {[20, 50, 100, 250, 500].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAmount(v)}
                      className={`px-3 py-1 rounded-full text-xs mono border transition-all ${
                        Number(amount) === v
                          ? "bg-[#D96C4A] text-white border-[#D96C4A]"
                          : "bg-white text-[#D96C4A] border-[#D96C4A]/30 hover:border-[#D96C4A]"
                      }`}
                      data-testid={`cash-request-quick-${v}`}
                    >
                      ${v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Name on card (optional)</Label>
                <input
                  className="underline-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="J. Doe"
                  data-testid="cash-request-name"
                />
              </div>
              <p
                className="text-[11px] text-[#4A5C50] leading-relaxed pt-3 border-t border-[#E5E0D8]"
                data-testid="cash-request-sms-consent"
              >
                By tapping <span className="font-semibold">Text me a verification code</span>, you
                agree to receive a one-time verification code and a single follow-up
                transactional SMS from <span className="font-semibold">Zcardon</span> at the number
                above. Message &amp; data rates may apply. Reply <span className="mono">STOP</span> to
                opt out, <span className="mono">HELP</span> for help. See our{" "}
                <a href="/support" className="underline">privacy policy</a>.
              </p>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="cash-request-cancel">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={busy}
                  className="rounded-xl bg-[#D96C4A] hover:bg-[#B04E2F] text-white disabled:opacity-50"
                  data-testid="cash-request-submit"
                >
                  {busy ? "Texting code…" : "Text me a verification code"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
        {stage === "otp" && (
          <>
            <DialogHeader>
              <DialogTitle className="serif text-3xl">Enter the 6-digit code</DialogTitle>
              <DialogDescription className="text-[#4A5C50]">
                We just texted a verification code to <span className="mono text-[#112318]">{otpToPhone}</span>.
                Enter it below to continue. Code expires in 10 minutes.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={verifyOtp} className="space-y-4 py-2">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Verification code</Label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="\d{6}"
                  className="underline-input mono text-3xl tracking-[0.4em] text-center"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="cash-request-otp"
                  autoFocus
                  required
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setStage("form"); setOtpCode(""); }}
                  className="text-xs text-[#4A5C50] link-ul"
                  data-testid="cash-request-otp-back"
                >
                  Wrong number? Go back.
                </button>
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={otpResendCooldown > 0 || busy}
                  className="text-xs text-[#D96C4A] disabled:text-[#4A5C50] disabled:cursor-not-allowed enabled:hover:underline mono"
                  data-testid="cash-request-otp-resend"
                >
                  {otpResendCooldown > 0 ? `Resend in ${otpResendCooldown}s` : "Resend code"}
                </button>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={busy || otpCode.length !== 6}
                  className="rounded-xl bg-[#D96C4A] hover:bg-[#B04E2F] text-white disabled:opacity-50"
                  data-testid="cash-request-otp-submit"
                >
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
        {stage === "paid" && result && (
          <CashRequestSuccess result={result} onClose={() => setOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CashRequestSuccess({ result, onClose }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="serif text-3xl">You're verified ✓</DialogTitle>
        <DialogDescription className="text-[#4A5C50]">
          Phone <span className="mono text-[#112318]">{result.to_phone}</span> is confirmed.
          Pay <span className="mono text-[#112318]">${Number(result.total_charged).toFixed(2)}</span> with
          your debit/credit card. As soon as Stripe confirms the payment, we'll text the funded card to your phone —
          tap-to-pay ready at any Zcardon merchant.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-3">
        <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-4 text-sm">
          <div className="flex justify-between"><span className="opacity-70">Loaded onto card</span><span className="mono">${Number(result.amount).toFixed(2)}</span></div>
          <div className="flex justify-between mt-1"><span className="opacity-70">Total to pay now</span><span className="mono">${Number(result.total_charged).toFixed(2)}</span></div>
        </div>
        <a
          href={result.checkout_url}
          target="_blank"
          rel="noreferrer"
          className="block w-full text-center rounded-xl bg-[#D96C4A] hover:bg-[#B04E2F] text-white py-3 font-medium transition-colors"
          data-testid="cash-request-open-checkout"
        >
          Pay ${Number(result.total_charged).toFixed(2)} with Stripe →
        </a>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} data-testid="cash-request-success-close">Close</Button>
      </DialogFooter>
    </>
  );
}



// Top countries by Twilio SMS volume / typical SaaS user-base. Order roughly
// by likely usage so US/UK/CA/etc. are at the top and easy to scroll to.
const COUNTRIES = [
  { code: "US", flag: "🇺🇸", dial: "+1",   name: "United States" },
  { code: "CA", flag: "🇨🇦", dial: "+1",   name: "Canada" },
  { code: "GB", flag: "🇬🇧", dial: "+44",  name: "United Kingdom" },
  { code: "AU", flag: "🇦🇺", dial: "+61",  name: "Australia" },
  { code: "DE", flag: "🇩🇪", dial: "+49",  name: "Germany" },
  { code: "FR", flag: "🇫🇷", dial: "+33",  name: "France" },
  { code: "ES", flag: "🇪🇸", dial: "+34",  name: "Spain" },
  { code: "IT", flag: "🇮🇹", dial: "+39",  name: "Italy" },
  { code: "NL", flag: "🇳🇱", dial: "+31",  name: "Netherlands" },
  { code: "SE", flag: "🇸🇪", dial: "+46",  name: "Sweden" },
  { code: "NO", flag: "🇳🇴", dial: "+47",  name: "Norway" },
  { code: "DK", flag: "🇩🇰", dial: "+45",  name: "Denmark" },
  { code: "FI", flag: "🇫🇮", dial: "+358", name: "Finland" },
  { code: "IE", flag: "🇮🇪", dial: "+353", name: "Ireland" },
  { code: "PT", flag: "🇵🇹", dial: "+351", name: "Portugal" },
  { code: "CH", flag: "🇨🇭", dial: "+41",  name: "Switzerland" },
  { code: "AT", flag: "🇦🇹", dial: "+43",  name: "Austria" },
  { code: "BE", flag: "🇧🇪", dial: "+32",  name: "Belgium" },
  { code: "PL", flag: "🇵🇱", dial: "+48",  name: "Poland" },
  { code: "CZ", flag: "🇨🇿", dial: "+420", name: "Czechia" },
  { code: "MX", flag: "🇲🇽", dial: "+52",  name: "Mexico" },
  { code: "BR", flag: "🇧🇷", dial: "+55",  name: "Brazil" },
  { code: "AR", flag: "🇦🇷", dial: "+54",  name: "Argentina" },
  { code: "CL", flag: "🇨🇱", dial: "+56",  name: "Chile" },
  { code: "CO", flag: "🇨🇴", dial: "+57",  name: "Colombia" },
  { code: "IN", flag: "🇮🇳", dial: "+91",  name: "India" },
  { code: "SG", flag: "🇸🇬", dial: "+65",  name: "Singapore" },
  { code: "HK", flag: "🇭🇰", dial: "+852", name: "Hong Kong" },
  { code: "JP", flag: "🇯🇵", dial: "+81",  name: "Japan" },
  { code: "KR", flag: "🇰🇷", dial: "+82",  name: "South Korea" },
  { code: "PH", flag: "🇵🇭", dial: "+63",  name: "Philippines" },
  { code: "ID", flag: "🇮🇩", dial: "+62",  name: "Indonesia" },
  { code: "MY", flag: "🇲🇾", dial: "+60",  name: "Malaysia" },
  { code: "TH", flag: "🇹🇭", dial: "+66",  name: "Thailand" },
  { code: "VN", flag: "🇻🇳", dial: "+84",  name: "Vietnam" },
  { code: "AE", flag: "🇦🇪", dial: "+971", name: "UAE" },
  { code: "SA", flag: "🇸🇦", dial: "+966", name: "Saudi Arabia" },
  { code: "IL", flag: "🇮🇱", dial: "+972", name: "Israel" },
  { code: "TR", flag: "🇹🇷", dial: "+90",  name: "Türkiye" },
  { code: "ZA", flag: "🇿🇦", dial: "+27",  name: "South Africa" },
  { code: "NG", flag: "🇳🇬", dial: "+234", name: "Nigeria" },
  { code: "EG", flag: "🇪🇬", dial: "+20",  name: "Egypt" },
  { code: "KE", flag: "🇰🇪", dial: "+254", name: "Kenya" },
  { code: "NZ", flag: "🇳🇿", dial: "+64",  name: "New Zealand" },
];

// Best-effort default-country detection. Walks 3 progressively-weaker signals:
//   1. navigator.language (e.g. "en-GB" → GB)
//   2. Intl resolved options' time zone region (e.g. "Europe/London" → GB)
//   3. Fallback to US.
function detectCountry() {
  if (typeof navigator === "undefined") return "US";
  try {
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || "";
    const region = lang.split("-")[1] || lang.split("_")[1];
    if (region) {
      const code = region.toUpperCase();
      if (COUNTRIES.some((c) => c.code === code)) return code;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const TZ_REGION = {
      "Europe/London": "GB", "Europe/Dublin": "IE", "Europe/Paris": "FR",
      "Europe/Berlin": "DE", "Europe/Madrid": "ES", "Europe/Rome": "IT",
      "Europe/Amsterdam": "NL", "Europe/Stockholm": "SE", "Europe/Oslo": "NO",
      "Europe/Copenhagen": "DK", "Europe/Helsinki": "FI", "Europe/Lisbon": "PT",
      "Europe/Zurich": "CH", "Europe/Vienna": "AT", "Europe/Brussels": "BE",
      "Europe/Warsaw": "PL", "Europe/Prague": "CZ",
      "America/Mexico_City": "MX", "America/Sao_Paulo": "BR",
      "America/Argentina/Buenos_Aires": "AR", "America/Santiago": "CL",
      "America/Bogota": "CO", "America/Toronto": "CA",
      "Asia/Kolkata": "IN", "Asia/Singapore": "SG", "Asia/Hong_Kong": "HK",
      "Asia/Tokyo": "JP", "Asia/Seoul": "KR", "Asia/Manila": "PH",
      "Asia/Jakarta": "ID", "Asia/Kuala_Lumpur": "MY", "Asia/Bangkok": "TH",
      "Asia/Ho_Chi_Minh": "VN", "Asia/Dubai": "AE", "Asia/Riyadh": "SA",
      "Asia/Jerusalem": "IL", "Europe/Istanbul": "TR",
      "Africa/Johannesburg": "ZA", "Africa/Lagos": "NG",
      "Africa/Cairo": "EG", "Africa/Nairobi": "KE",
      "Pacific/Auckland": "NZ", "Australia/Sydney": "AU",
    };
    const fromTz = TZ_REGION[tz];
    if (fromTz && COUNTRIES.some((c) => c.code === fromTz)) return fromTz;
  } catch {
    // ignore — fall through
  }
  return "US";
}


// "Lost your card link?" recovery flow — phone OTP gates a list of every
// active cash-request card on file plus any Stripe-paid cards we can rebuild.
function FindMyCardButton() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(() => detectCountry());
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState("form"); // form | otp | results
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [cards, setCards] = useState([]);
  const [recoveredCount, setRecoveredCount] = useState(0);

  const reset = () => {
    setStage("form"); setRequestId(null); setOtpCode("");
    setCards([]); setRecoveredCount(0); setBusy(false);
  };

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!phone.trim()) { toast.error("Enter your phone number"); return; }
    setBusy(true);
    try {
      let composed = "";
      try {
        const parsed = parsePhoneNumberFromString(phone, country);
        if (parsed?.isValid()) composed = parsed.number;
      } catch { /* ignore */ }
      if (!composed) {
        toast.error("That phone number doesn't look right");
        return;
      }
      const r = await api.post("/cash-request/find/start", { phone: composed });
      setRequestId(r.data.request_id);
      setStage("otp");
      toast.success(`Code texted to ${r.data.to_phone}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send verification code");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (!/^\d{4,8}$/.test(otpCode.trim())) {
      toast.error("Enter the 6-digit code we just texted");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/cash-request/find/verify", {
        request_id: requestId, code: otpCode.trim(),
      });
      setCards(r.data.cards || []);
      setRecoveredCount(r.data.newly_recovered || 0);
      setStage("results");
      if ((r.data.cards || []).length === 0) {
        toast.message("No cards found for this phone");
      } else if (r.data.newly_recovered) {
        toast.success(`Recovered ${r.data.newly_recovered} card${r.data.newly_recovered > 1 ? "s" : ""}!`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-3 fade-up-d2">
        <button
          type="button"
          onClick={() => { reset(); setOpen(true); }}
          className="text-sm text-[#4A5C50] hover:text-[#184A30] underline underline-offset-4 decoration-[#184A30]/30 hover:decoration-[#184A30]"
          data-testid="find-card-trigger"
        >
          {t("landing.find.cta")} →
        </button>
      </div>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-md" data-testid="find-card-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">{t("landing.find.dialog_title")}</DialogTitle>
            <DialogDescription className="text-[#4A5C50]">
              {stage === "form" && t("landing.find.dialog_desc")}
              {stage === "otp" && "Enter the 6-digit code we just texted you."}
              {stage === "results" && "Tap a card to open its tap-to-pay screen."}
            </DialogDescription>
          </DialogHeader>

          {stage === "form" && (
            <form onSubmit={sendOtp} className="space-y-4" data-testid="find-card-form">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Country</Label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="underline-input mono w-full mt-1 bg-transparent"
                  data-testid="find-card-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name} (+{c.dial})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Phone</Label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const fmt = new AsYouType(country);
                    setPhone(fmt.input(e.target.value));
                  }}
                  placeholder={(getExampleNumber(country, examples)?.formatNational?.()) || ""}
                  autoComplete="tel"
                  className="underline-input mono w-full mt-1"
                  data-testid="find-card-phone"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white h-11"
                data-testid="find-card-send-otp"
              >
                {busy ? "Sending code…" : "Text me a code"}
              </Button>
              <p className="text-[11px] text-[#4A5C50] leading-relaxed pt-2" data-testid="find-card-sms-consent">
                By tapping the button above, you agree to receive a one-time verification code from <span className="font-semibold">Zcardon</span> at this number. Reply <span className="mono">STOP</span> to opt out.
              </p>
            </form>
          )}

          {stage === "otp" && (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="find-card-otp-form">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">6-digit code</Label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="underline-input mono w-full mt-1 text-2xl tracking-[0.5em] text-center"
                  autoFocus
                  data-testid="find-card-otp-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setStage("form"); setOtpCode(""); }}
                  className="rounded-xl"
                  data-testid="find-card-back"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={busy || otpCode.length < 4}
                  className="flex-1 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white h-11"
                  data-testid="find-card-verify"
                >
                  {busy ? "Checking…" : "Verify and find cards"}
                </Button>
              </div>
            </form>
          )}

          {stage === "results" && (
            <div className="space-y-3" data-testid="find-card-results">
              {recoveredCount > 0 && (
                <div className="rounded-xl border border-[#184A30]/30 bg-[#184A30]/5 p-3 text-xs text-[#184A30] mono">
                  ✓ Recovered {recoveredCount} paid card{recoveredCount > 1 ? "s" : ""} that vanished from our records — your money is safe.
                </div>
              )}
              {cards.length === 0 && (
                <div className="text-center py-8">
                  <p className="serif text-xl text-[#112318]">No cards found</p>
                  <p className="text-sm text-[#4A5C50] mt-2">
                    We couldn't find any cash-request cards for this phone.
                    Was the phone different when you paid?
                  </p>
                  <a
                    href="mailto:support@zcardon.com"
                    className="inline-block mt-4 text-sm text-[#184A30] underline"
                    data-testid="find-card-contact-support"
                  >
                    Contact support
                  </a>
                </div>
              )}
              {cards.length > 0 && (
                <ul className="divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-xl">
                  {cards.map((c) => {
                    const isActive = c.status === "active";
                    const href = `/cash-request/${c.id}${c.session_id ? `?session_id=${encodeURIComponent(c.session_id)}` : ""}`;
                    return (
                      <li key={c.id}>
                        <Link
                          to={href}
                          onClick={() => setOpen(false)}
                          className="flex items-center gap-3 p-3 hover:bg-[#FBF9F6]"
                          data-testid={`find-card-row-${c.id.slice(0, 8)}`}
                        >
                          <div className={`w-2 h-2 rounded-full ${isActive ? "bg-[#184A30]" : "bg-[#CBA35C]"}`} />
                          <div className="flex-1 min-w-0">
                            <p className="mono text-sm text-[#112318]">
                              {isActive ? `•••• ${c.last4}` : "Pending payment"}
                            </p>
                            <p className="text-[10px] mono text-[#4A5C50] mt-0.5">
                              {isActive
                                ? `$${Number(c.balance_usd).toFixed(2)} · exp ${c.expiry || "--/--"}`
                                : "Tap to finalize"}
                            </p>
                          </div>
                          <ArrowRight size={16} className="text-[#4A5C50]" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Email-only waitlist form for the "Q3 2026 · In approval" Visa upgrade
 * panel. Drops the email into `tap_to_pay_waitlist` and shows the
 * running size (only once ≥ 5 to avoid an anti-social "Be the first!"
 * empty-state). Idempotent — re-submitting the same email is harmless
 * and gives the user a friendly "already on the list" confirmation.
 */
function TapToPayWaitlistForm() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [size, setSize] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/waitlist/tap-to-pay", { email: trimmed, source: "landing" });
      setJoined(true);
      setSize(r?.data?.waitlist_size ?? null);
      if (r?.data?.already_on_waitlist) {
        toast.success("You're already on the list — we'll be in touch when real Visa cards ship.");
      } else {
        toast.success("You're on the waitlist. We'll email you when real Visa cards ship.");
      }
    } catch {
      toast.error("Couldn't save your email — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (joined) {
    return (
      <div
        className="mt-4 rounded-xl border border-[#D96C4A]/40 bg-white p-3 text-sm text-[#112318]"
        data-testid="waitlist-confirmation"
      >
        <p className="font-semibold text-[#B04E2F]">✓ You're on the list</p>
        <p className="text-xs text-[#4A5C50] mt-0.5">
          We'll email you the moment real Visa tap-to-pay ships.
          {size != null && size >= 5 ? <> Joining <b>{size.toLocaleString()}</b> other early-access members.</> : null}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-4 flex flex-col sm:flex-row gap-2"
      data-testid="waitlist-form"
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        className="flex-1 rounded-xl border border-[#D96C4A]/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#D96C4A]"
        data-testid="waitlist-email-input"
        required
      />
      <Button
        type="submit"
        disabled={submitting}
        className="bg-[#D96C4A] hover:bg-[#B04E2F] text-white rounded-xl text-sm font-semibold whitespace-nowrap"
        data-testid="waitlist-submit"
      >
        {submitting ? "Saving…" : "Notify me at launch"}
      </Button>
    </form>
  );
}

